import {isEmpty as _isEmpty, isNumber as _isNumber} from 'lodash';
import {Header} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import React from 'react';
import {useQuery} from '@apollo/client';

import {createForm, renderModalError} from '@utils/forms';
import {GetEventsForReportMultipleAsWorkingAndInvoiceable} from '@graphql/events/query';
import {getLink} from '@appComponents/Link';
import {showModal, showErrorModal} from '@utils/modal';
import ReportMultipleEventsAsWorkingModel from '@modules/notifications/forms/ReportMultipleEventsAsWorkingModel';
import ReportMultipleEventsAsWorkingForm from '@modules/notifications/forms/ReportMultipleEventsAsWorkingForm';
import {
    INVALID_SELECTION_MULTIPLE_EVENTS_MESSAGE,
    INVALID_SELECTION_MULTIPLE_EVENTS_REPORT_WORKING_INVOICEABLE,
    REPORT_MULTIPLE_AS_WORKING_AND_INVOICEABLE_MESSAGE,
} from '@modules/events/utils/reportMultipleAsWorkingAndInvoiceableHelper';

import {EventsReportedAsWorkingAndInvoiceableTable} from './EventsMultiple/EventsReportedAsWorkingAndInvoiceableTable';

const ReportMultipleAsWorkingAndInvoiceableModalContent = ({eventIds = []}) => {
    const EVENTS_LIMIT = 100;

    const errorHandler = (message = null)  => {
        showModal({
            isVisible: true,
            content: renderModalError(
                REPORT_MULTIPLE_AS_WORKING_AND_INVOICEABLE_MESSAGE,
                getLink('events.index'),
                message,
                'Back to Event Scheduling'
            ),
            size: 'tiny',
        });
    };

    if (!eventIds.length) {
        errorHandler('No events selected');

        return null;
    }

    if (eventIds.length > EVENTS_LIMIT) {
        errorHandler(`You can report maximum of ${EVENTS_LIMIT} events at once, ${eventIds.length} selected.`);

        return null;
    }

    eventIds.forEach((eid) => {
        if (!_isNumber(eid)) {
            errorHandler('Wrong event id provided, all event ids must be numeric!');

            return null;
        }
    });

    const {
        data: {
            eventListForMultipleView,
        } = {},
        error,
        loading: isLoading,
    } = useQuery(GetEventsForReportMultipleAsWorkingAndInvoiceable, {
        fetchPolicy: 'no-cache',
        variables: {events: eventIds},
    });

    if (!_isEmpty(error)) {
        errorHandler('An error occurred while fetching events data.');

        return null;
    }

    if (!isLoading) {
        const hasInvalidEvent = eventListForMultipleView.events.some(event => {
            const hasAnyInvoiceableProduct = event.event_contents?.some(content =>
                content.products?.some(product => product.is_invoiceable)
            );

            return event.is_unreportable && !hasAnyInvoiceableProduct;
        });

        if (hasInvalidEvent) {
            showErrorModal({
                header: INVALID_SELECTION_MULTIPLE_EVENTS_REPORT_WORKING_INVOICEABLE,
                text: INVALID_SELECTION_MULTIPLE_EVENTS_MESSAGE,
            });
        }
    }

    const ReportMultipleForm = createForm(
        ReportMultipleEventsAsWorkingModel,
        ReportMultipleEventsAsWorkingForm,
        {
            optionsVariables: {
                events: eventIds,
            },
        }
    );

    return (
        <>
            <Header content={REPORT_MULTIPLE_AS_WORKING_AND_INVOICEABLE_MESSAGE} />
            <EventsReportedAsWorkingAndInvoiceableTable
                data={eventListForMultipleView}
                isLoading={isLoading}
            />
            <p className='--marginTop-10 --marginLeft-1-5em --marginBottom-0'>
                Are you sure that these events were delivered without any issues and should be reported
                as working and invoiceable for all streams and products?
            </p>
            <ReportMultipleForm />
        </>
    );
};

ReportMultipleAsWorkingAndInvoiceableModalContent.propTypes = {
    eventIds: PropTypes.array.isRequired,
};

export default ReportMultipleAsWorkingAndInvoiceableModalContent;
